// AdminAddTool.jsx - Updated with GST and Weight fields
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Box,
  Alert,
  CircularProgress,
  Input,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  CardMedia,
  InputAdornment,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link, useNavigate, useParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { getHeader } from "../../utils/util";
import { API_ENDPOINT } from "../../constants/constants";
import BackBtn from "../widgets/BackBtn";
import ImageCropDialog from "./ImageCropDialog";
import useApiEp from "../hooks/useApiEp";

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.5, 3),
  fontSize: "1.1rem",
}));

const SuccessDialogContent = styled(DialogContent)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(4),
}));

const SuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  fontSize: 64,
  color: theme.palette.success.main,
  marginBottom: theme.spacing(2),
}));

const ErrorIconStyled = styled(ErrorIcon)(({ theme }) => ({
  fontSize: 64,
  color: theme.palette.error.main,
  marginBottom: theme.spacing(2),
}));

const AdminAddTool = () => {
  const { id } = useParams();

  const {
    data: categories,
    isLoading,
    isError,
  } = useApiEp(`user/product-categories`);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    mrp: "",
    sellingPrice: "",
    discount: "",
    category: "",
    stock: "",
    gst: "",
    weight: "",
    weightUnit: "kg",
  });
  const [imagePreviews, setImagePreviews] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  // Cropper dialog state
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropSource, setCropSource] = useState(null);
  const [cropQueue, setCropQueue] = useState([]);
  const [recropTargetId, setRecropTargetId] = useState(null);

  useEffect(() => {
    if (id) {
      fetchTool();
    } else {
      setImagePreviews([]);
    }
  }, [id]);

  const fetchTool = async () => {
    setFetchLoading(true);
    setError("");
    try {
      const authHeaders = getHeader ? getHeader() : {};
      const response = await fetch(`${API_ENDPOINT}admin/tool/${id}`, {
        headers: authHeaders.headers || authHeaders,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data) {
        throw new Error("Tool not found!");
      }

      setFormData({
        name: data.name || "",
        description: data.description || "",
        mrp: data.mrp?.toString() || "",
        sellingPrice: data.sellingPrice?.toString() || "",
        discount: data.discount?.toString() || "",
        category: data.category || "",
        stock: data.stock?.toString() || "",
        gst: data.gst?.toString() || "",
        weight: data.weight?.toString() || "",
        weightUnit: data.weightUnit || "kg",
      });

      const initialPreviews = Array.isArray(data.images)
        ? data.images.map((url, idx) => ({
            id: `existing-${idx}-${Date.now()}`,
            preview: url,
            isNew: false,
          }))
        : data.imageUrl
        ? [{ id: "existing", preview: data.imageUrl, isNew: false }]
        : [];

      setImagePreviews(initialPreviews);
    } catch (err) {
      console.error("Error fetching tool:", err);
      const errorMsg = err.message || "Failed to fetch tool. Please try again.";
      setError(errorMsg);
      setDialogMessage(errorMsg);
      setShowErrorDialog(true);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError("");
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setError("");

    const validFiles = [];
    const invalidReasons = [];

    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        invalidReasons.push(`${file.name}: not an image`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        invalidReasons.push(`${file.name}: exceeds 5MB`);
        continue;
      }
      validFiles.push(file);
    }

    if (invalidReasons.length > 0) {
      setError(`Some files skipped: ${invalidReasons.join(", ")}`);
    }

    if (validFiles.length > 0) {
      setCropQueue((prev) => [...prev, ...validFiles]);
    }

    e.target.value = "";
  };

  useEffect(() => {
    if (cropDialogOpen) return;
    if (cropQueue.length === 0) return;

    const nextFile = cropQueue[0];
    const objectUrl = URL.createObjectURL(nextFile);
    setCropSource(objectUrl);
    setCropDialogOpen(true);
  }, [cropQueue, cropDialogOpen]);

  const handleCropSave = (croppedFile) => {
    const previewUrl = URL.createObjectURL(croppedFile);

    if (recropTargetId) {
      setImagePreviews((prev) =>
        prev.map((img) => {
          if (img.id === recropTargetId) {
            if (img.isNew && img.preview && img.preview.startsWith("blob:")) {
              try {
                URL.revokeObjectURL(img.preview);
              } catch (e) {}
            }
            return {
              ...img,
              file: croppedFile,
              preview: previewUrl,
              isNew: true,
            };
          }
          return img;
        })
      );
      setRecropTargetId(null);
    } else {
      setImagePreviews((prev) => [
        ...prev,
        {
          id: Date.now() + Math.floor(Math.random() * 1000),
          file: croppedFile,
          preview: previewUrl,
          isNew: true,
        },
      ]);
    }

    setCropDialogOpen(false);
    if (cropSource && cropSource.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(cropSource);
      } catch (e) {}
    }
    setCropSource(null);
    setCropQueue((prev) => prev.slice(1));
  };

  const handleCropCancel = () => {
    setCropDialogOpen(false);
    if (cropSource && cropSource.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(cropSource);
      } catch (e) {}
    }
    setCropSource(null);
    setCropQueue((prev) => prev.slice(1));
    setRecropTargetId(null);
  };

  const handleRecropExisting = (img) => {
    setError("");
    const src = img.preview;
    setRecropTargetId(img.id);
    setCropSource(src);
    setCropDialogOpen(true);
  };

  const handleRemoveImage = (id) => {
    setImagePreviews((prev) => {
      const removed = prev.find((img) => img.id === id);
      if (removed) {
        if (
          removed.isNew &&
          removed.preview &&
          removed.preview.startsWith("blob:")
        ) {
          try {
            URL.revokeObjectURL(removed.preview);
          } catch (e) {}
        }
      }
      return prev.filter((img) => img.id !== id);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // Basic validation
    if (
      !formData.name ||
      !formData.description ||
      !formData.mrp ||
      !formData.sellingPrice ||
      !formData.category ||
      !formData.stock
    ) {
      const errorMsg = "Please fill in all required fields.";
      setError(errorMsg);
      setDialogMessage(errorMsg);
      setShowErrorDialog(true);
      setLoading(false);
      return;
    }

    if (imagePreviews.length === 0) {
      const errorMsg = "At least one image is required.";
      setError(errorMsg);
      setDialogMessage(errorMsg);
      setShowErrorDialog(true);
      setLoading(false);
      return;
    }

    if (
      isNaN(formData.mrp) ||
      isNaN(formData.sellingPrice) ||
      isNaN(formData.stock) ||
      parseFloat(formData.mrp) <= 0 ||
      parseFloat(formData.sellingPrice) <= 0 ||
      parseInt(formData.stock) < 0
    ) {
      const errorMsg =
        "MRP and selling price must be positive numbers, and stock must be non-negative.";
      setError(errorMsg);
      setDialogMessage(errorMsg);
      setShowErrorDialog(true);
      setLoading(false);
      return;
    }

    if (parseFloat(formData.sellingPrice) > parseFloat(formData.mrp)) {
      const errorMsg = "Selling price must be less than or equal to MRP.";
      setError(errorMsg);
      setDialogMessage(errorMsg);
      setShowErrorDialog(true);
      setLoading(false);
      return;
    }

    // Validate GST if provided
    if (
      formData.gst &&
      (isNaN(formData.gst) ||
        parseFloat(formData.gst) < 0 ||
        parseFloat(formData.gst) > 100)
    ) {
      const errorMsg = "GST must be a number between 0 and 100.";
      setError(errorMsg);
      setDialogMessage(errorMsg);
      setShowErrorDialog(true);
      setLoading(false);
      return;
    }

    // Validate weight if provided
    if (
      formData.weight &&
      (isNaN(formData.weight) || parseFloat(formData.weight) <= 0)
    ) {
      const errorMsg = "Weight must be a positive number.";
      setError(errorMsg);
      setDialogMessage(errorMsg);
      setShowErrorDialog(true);
      setLoading(false);
      return;
    }

    // Create FormData
    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("description", formData.description);
    formDataToSend.append("mrp", formData.mrp);
    formDataToSend.append("sellingPrice", formData.sellingPrice);
    formDataToSend.append("discount", formData.discount || "");
    formDataToSend.append("category", formData.category);
    formDataToSend.append("stock", formData.stock);
    formDataToSend.append("gst", formData.gst || "");
    formDataToSend.append("weight", formData.weight || "");
    formDataToSend.append("weightUnit", formData.weightUnit || "kg");

    const keptUrls = imagePreviews
      .filter((img) => !img.isNew)
      .map((img) => img.preview);
    if (keptUrls.length > 0) {
      formDataToSend.append("keptImageUrls", JSON.stringify(keptUrls));
    }

    imagePreviews
      .filter((img) => img.isNew && img.file)
      .forEach((img) => {
        formDataToSend.append("image", img.file);
      });

    try {
      const endpoint = id ? `admin/edit-tool/${id}` : "admin/add-tool";
      const authHeaders = getHeader ? getHeader() : {};
      const headers = {};
      if (authHeaders.headers) {
        Object.keys(authHeaders.headers).forEach((key) => {
          if (key.toLowerCase() !== "content-type") {
            headers[key] = authHeaders.headers[key];
          }
        });
      }

      const response = await fetch(`${API_ENDPOINT}${endpoint}`, {
        method: "POST",
        body: formDataToSend,
        headers,
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
          if (errorData.status === "validation_error" && errorData.errors) {
            errorMessage = errorData.errors
              .map((err) => `${err.field}: ${err.message}`)
              .join("; ");
          }
        } catch (parseErr) {
          console.warn("Could not parse error response:", parseErr);
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log(id ? "Tool updated:" : "Tool added:", result);

      const isEdit = !!id;
      setSuccess(
        isEdit ? "Tool updated successfully!" : "Tool added successfully!"
      );
      setDialogMessage(
        isEdit
          ? "Tool has been updated successfully!"
          : "Tool has been added successfully!"
      );
      setShowSuccessDialog(true);

      if (!isEdit) {
        imagePreviews.forEach((img) => {
          if (img.isNew && img.preview && img.preview.startsWith("blob:")) {
            try {
              URL.revokeObjectURL(img.preview);
            } catch (e) {}
          }
        });
        setImagePreviews([]);
        setFormData({
          name: "",
          description: "",
          mrp: "",
          sellingPrice: "",
          discount: "",
          category: "",
          stock: "",
          gst: "",
          weight: "",
          weightUnit: "kg",
        });
      }
    } catch (err) {
      console.error(id ? "Error updating tool:" : "Error adding tool:", err);
      const errorMsg = err.message || "Failed to save tool. Please try again.";
      setError(errorMsg);
      setDialogMessage(errorMsg);
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessDialog(false);
    navigate("/home/tools");
  };

  const handleErrorClose = () => {
    setShowErrorDialog(false);
  };

  const isEdit = !!id;
  const pageTitle = isEdit
    ? "Edit Sericulture Tool"
    : "Add New Sericulture Tool";
  const submitText = isEdit ? "Update Tool" : "Add Tool";
  const submitLoadingText = isEdit ? "Updating..." : "Adding...";

  return (
    <Container maxWidth="md">
      <BackBtn />
      <StyledPaper elevation={3}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          align="center"
          color="primary"
        >
          {pageTitle}
        </Typography>
        <Typography
          variant="body1"
          align="center"
          color="textSecondary"
          paragraph
        >
          {isEdit
            ? "Update the details of the existing sericulture tool."
            : "Add tools for purchase by sericulture farmers. Ensure details are accurate for better inventory management."}
        </Typography>

        <Typography
          variant="body2"
          align="center"
          color="primary"
          sx={{ mb: 2, fontStyle: "italic" }}
        >
          💡 For best display in mobile app, please upload high-quality images
          that will look good when cropped to square format (1:1 ratio).
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Product Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description *"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={4}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="MRP (₹) *"
                name="mrp"
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                value={formData.mrp}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Selling Price (₹) *"
                name="sellingPrice"
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                value={formData.sellingPrice}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Discount (%)"
                name="discount"
                type="number"
                inputProps={{ step: "0.01", min: "0", max: "100" }}
                value={formData.discount}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="GST (%)"
                name="gst"
                type="number"
                inputProps={{ step: "0.01", min: "0", max: "100" }}
                value={formData.gst}
                onChange={handleChange}
                variant="outlined"
                helperText="GST percentage (optional)"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Stock Quantity *"
                name="stock"
                type="number"
                inputProps={{ min: "0" }}
                value={formData.stock}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Category *</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={(e) => {
                    if (e.target.value === "__add_new__") {
                      navigate("/home/categories"); // react-router
                      return;
                    }
                    handleChange(e);
                  }}
                  label="Category *"
                >
                  {categories?.map((cat, index) => (
                    <MenuItem key={index} value={cat.category}>
                      {cat.category}
                    </MenuItem>
                  ))}

                  {/* Divider */}
                  <MenuItem disabled>
                    <em>──────────</em>
                  </MenuItem>

                  {/* Add New Category Button */}
                  <MenuItem
                    value="__add_new__"
                    sx={{
                      color: "primary.main",
                      fontWeight: 600,
                    }}
                  >
                    ➕ Add New Category
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Weight"
                name="weight"
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                value={formData.weight}
                onChange={handleChange}
                variant="outlined"
                helperText="Item weight (optional)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Weight Unit</InputLabel>
                <Select
                  name="weightUnit"
                  value={formData.weightUnit}
                  onChange={handleChange}
                  label="Weight Unit"
                >
                  <MenuItem value="kg">Kilograms (kg)</MenuItem>
                  {/* <MenuItem value="g">Grams (g)</MenuItem>
                  <MenuItem value="lb">Pounds (lb)</MenuItem>
                  <MenuItem value="oz">Ounces (oz)</MenuItem> */}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Tool Images
              </Typography>

              {imagePreviews.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Image Previews:
                  </Typography>
                  <Grid container spacing={2}>
                    {imagePreviews.map((img) => (
                      <Grid item xs={6} sm={4} key={img.id}>
                        <Box sx={{ position: "relative" }}>
                          <CardMedia
                            component="img"
                            src={img.preview}
                            alt="Tool image"
                            sx={{
                              width: 150,
                              height: 150,
                              borderRadius: 1,
                              objectFit: "cover",
                              cursor: "pointer",
                              border: (theme) =>
                                `1px solid ${theme.palette.divider}`,
                            }}
                            onClick={() => handleRecropExisting(img)}
                          />
                          <IconButton
                            aria-label="remove image"
                            onClick={() => handleRemoveImage(img.id)}
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              backgroundColor: (theme) =>
                                theme.palette.error.main,
                              color: "white",
                              "&:hover": {
                                backgroundColor: (theme) =>
                                  theme.palette.error.dark,
                              },
                            }}
                            size="small"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              <Input
                fullWidth
                inputProps={{ "aria-label": "Upload images" }}
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageChange}
              />
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: "block", mt: 1 }}
              >
                Upload multiple tool images (JPG/PNG up to 5MB each). Click an
                existing preview to recrop/replace it.
              </Typography>
            </Grid>
          </Grid>

          <SubmitButton
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading || fetchLoading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? submitLoadingText : submitText}
          </SubmitButton>
        </Box>
      </StyledPaper>

      <ImageCropDialog
        open={cropDialogOpen}
        initialSrc={cropSource}
        onCancel={handleCropCancel}
        onSave={handleCropSave}
      />

      <Dialog
        open={showSuccessDialog}
        onClose={handleSuccessClose}
        aria-labelledby="success-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="success-dialog-title" sx={{ textAlign: "center" }}>
          Success!
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleSuccessClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <SuccessDialogContent>
          <SuccessIcon />
          <DialogContentText>
            <Typography variant="h6" gutterBottom>
              {isEdit
                ? "Tool Updated Successfully!"
                : "Tool Added Successfully!"}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {dialogMessage}
            </Typography>
          </DialogContentText>
        </SuccessDialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSuccessClose}
            size="large"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showErrorDialog}
        onClose={handleErrorClose}
        aria-labelledby="error-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="error-dialog-title" sx={{ textAlign: "center" }}>
          Error
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleErrorClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <SuccessDialogContent>
          <ErrorIconStyled />
          <DialogContentText>
            <Typography variant="h6" gutterBottom color="error">
              Failed to {isEdit ? "Update" : "Add"} Tool
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {dialogMessage}
            </Typography>
          </DialogContentText>
        </SuccessDialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            color="error"
            onClick={handleErrorClose}
            size="large"
          >
            Try Again
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminAddTool;
